import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { cn } from '@/lib/utils'

type Department =
  | 'sound' | 'lighting' | 'staging' | 'backline' | 'video'
  | 'power' | 'rigging' | 'hospitality' | 'dressing_room' | 'catering'

type Category = 'non_negotiable' | 'negotiable' | 'specialist'
type SubPolicy = 'none' | 'equivalent' | 'exceed_ok' | 'human_decision'

interface RiderItem {
  id: string
  department: Department
  name: string
  specification: string
  quantity: number
  category: Category
  substitutionPolicy: SubPolicy
  notes: string | null
  internalNotes: string | null
  assignedReviewer: string
  weightKgEach: number | null
  volumeM3Each: number | null
  sortOrder: number
}

interface RiderTemplate {
  id: string
  name: string
  description: string | null
  isActive: boolean
  items: RiderItem[]
}

const DEPTS: Department[] = [
  'sound', 'lighting', 'staging', 'backline', 'video',
  'power', 'rigging', 'hospitality', 'dressing_room', 'catering',
]
const CATEGORIES: Category[] = ['non_negotiable', 'negotiable', 'specialist']
const POLICIES: SubPolicy[] = ['none', 'equivalent', 'exceed_ok', 'human_decision']

const CAT_COLOUR: Record<Category, string> = {
  non_negotiable: 'bg-rose-100 text-rose-700',
  negotiable: 'bg-blue-100 text-blue-700',
  specialist: 'bg-purple-100 text-purple-700',
}

export function RiderPage() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)

  const { data, error, isLoading } = useQuery<RiderTemplate, ApiClientError>({
    queryKey: ['rider-active'],
    queryFn: () => artistFetch<RiderTemplate>('/rider'),
    retry: false,
  })

  const noRider = error?.status === 404

  async function removeItem(itemId: string) {
    if (!confirm('Remove this item from the rider?')) return
    try {
      await artistFetch(`/rider/items/${itemId}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['rider-active'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  if (noRider) {
    return <CreateFirstRider onCreated={() => qc.invalidateQueries({ queryKey: ['rider-active'] })} />
  }

  const grouped = new Map<Department, RiderItem[]>()
  for (const item of data?.items ?? []) {
    if (!grouped.has(item.department)) grouped.set(item.department, [])
    grouped.get(item.department)!.push(item)
  }

  return (
    <div>
      <PageHeader
        title={data?.name ?? 'Technical Rider'}
        subtitle="Structured equipment list by department. Drives the advancing questionnaire and equipment manifest."
        actions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'Cancel' : 'Add item'}
          </button>
        }
      />

      {adding && data && (
        <AddItemForm
          templateId={data.id}
          onAdded={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['rider-active'] })
          }}
        />
      )}

      <AsyncBoundary isLoading={isLoading} error={noRider ? null : error}>
        {(data?.items ?? []).length === 0 ? (
          <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
            No items in the rider yet. Add your first piece of equipment.
          </div>
        ) : (
          <div className="space-y-6">
            {DEPTS.filter((d) => grouped.has(d)).map((dept) => (
              <div key={dept}>
                <h2 className="mb-2 font-display text-xl capitalize">
                  {dept.replace(/_/g, ' ')}
                </h2>
                <div className="space-y-2">
                  {grouped.get(dept)!.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start justify-between gap-3 rounded-lg border border-ovation-ink/10 bg-white p-4"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.name}</span>
                          <span className="text-xs text-ovation-muted">
                            x{item.quantity}
                          </span>
                          <span
                            className={cn(
                              'rounded px-1.5 py-0.5 text-[10px] uppercase',
                              CAT_COLOUR[item.category],
                            )}
                          >
                            {item.category.replace(/_/g, ' ')}
                          </span>
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-600">
                            {item.substitutionPolicy.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-ovation-muted">
                          {item.specification}
                        </div>
                        {item.notes && (
                          <div className="mt-1 text-xs text-ovation-muted">
                            Note: {item.notes}
                          </div>
                        )}
                        <div className="mt-1 flex gap-3 text-xs text-ovation-muted">
                          <span>
                            Reviewer: {item.assignedReviewer.replace(/_/g, ' ')}
                          </span>
                          {item.weightKgEach && (
                            <span>{item.weightKgEach}kg each</span>
                          )}
                          {item.volumeM3Each && (
                            <span>{item.volumeM3Each}m³ each</span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="shrink-0 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  )
}

function CreateFirstRider({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('Standard show rider 2026')
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    try {
      await artistFetch('/rider', {
        method: 'POST',
        body: { name, isActive: true },
      })
      onCreated()
    } catch {
      alert('Failed to create rider template')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PageHeader title="Technical Rider" subtitle="No rider template exists yet. Create one to start defining equipment requirements." />
      <form onSubmit={onSubmit} className="max-w-md space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
        <div>
          <label className="mb-1 block text-sm font-medium">Template name</label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          {submitting ? 'Creating…' : 'Create rider template'}
        </button>
      </form>
    </div>
  )
}

function AddItemForm({
  templateId,
  onAdded,
}: {
  templateId: string
  onAdded: () => void
}) {
  const [dept, setDept] = useState<Department>('sound')
  const [name, setName] = useState('')
  const [spec, setSpec] = useState('')
  const [qty, setQty] = useState(1)
  const [cat, setCat] = useState<Category>('negotiable')
  const [subPol, setSubPol] = useState<SubPolicy>('equivalent')
  const [reviewer, setReviewer] = useState('foh_engineer')
  const [notes, setNotes] = useState('')
  const [weight, setWeight] = useState('')
  const [volume, setVolume] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        department: dept,
        name,
        specification: spec,
        quantity: qty,
        category: cat,
        substitutionPolicy: subPol,
        assignedReviewer: reviewer,
        minimumSpec: {},
        preferredSpec: {},
      }
      if (notes) body.notes = notes
      if (weight) body.weightKgEach = parseFloat(weight)
      if (volume) body.volumeM3Each = parseFloat(volume)
      await artistFetch(`/rider/${templateId}/items`, {
        method: 'POST',
        body,
      })
      onAdded()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Department</label>
          <select value={dept} onChange={(e) => setDept(e.target.value as Department)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm">
            {DEPTS.map((d) => <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Category</label>
          <select value={cat} onChange={(e) => setCat(e.target.value as Category)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Substitution policy</label>
          <select value={subPol} onChange={(e) => setSubPol(e.target.value as SubPolicy)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm">
            {POLICIES.map((p) => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Item name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="e.g. LED wash fixture" />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Qty</label>
            <input type="number" min={1} value={qty} onChange={(e) => setQty(Number(e.target.value))} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Weight (kg)</label>
            <input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="per unit" />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Vol (m³)</label>
            <input type="number" step="0.01" value={volume} onChange={(e) => setVolume(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="per unit" />
          </div>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Technical specification</label>
        <textarea required value={spec} onChange={(e) => setSpec(e.target.value)} rows={2} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="LED PAR, minimum 80W, RGBW, DMX512 compatible" />
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Notes (visible to venue)</label>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Adding…' : 'Add item'}
      </button>
    </form>
  )
}
